import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Card } from '@nextiva/next-ui';

import { CANCEL_LABEL, OK_LABEL } from 'constant';
import { DialogComponent } from 'UIElements';
import { PaymentMethodInfo } from '../..';
import './PaymentMethod.scss';
import PaymentMethodActions from './PaymentMethodActions/PaymentMethodActions';
import {
  isApplePay,
  isCreditCard,
} from 'validations/paymentValidations/paymentValidations';
import { getDeleteDialogText } from 'getters/paymentGetters/paymentGetters';

export const blockName = 'payment-method';

/**
 * PaymentMethod component.
 *
 * @property {function} addAlertDetails - Callback function to add alert details.
 * @property {string} [billToSiteNumber] - The billing site number (optional).
 * @property {number} corpAcctNbr - The corporate account number.
 * @property {boolean} [isDefault] - Indicates if the payment method is set as default (optional).
 * @property {boolean} [isSummaryPage] - Indicates if the component is rendered on the summary page (optional).
 * @property {string} [nextInvoiceDate] - The date of the next invoice (optional).
 * @property {object} paymentMethod - The payment method object containing details to display.
 * @property {function} startDeleteCreditCard - Callback function to start the delete credit card process.
 * @property {function} startSetDefaultPayment - Callback function to set the payment method as default.
 */
const PaymentMethod = ({
  addAlertDetails,
  billToSiteNumber,
  corpAcctNbr,
  isDefault,
  isSummaryPage,
  nextInvoiceDate,
  paymentMethod,
  startDeleteCreditCard,
  startSetDefaultPayment,
}) => {
  const { paymentMethodUuid } = paymentMethod;

  const [showDialog, setShowDialog] = useState(false);

  /**
   * Triggers the functionality to delete a credit card and hides the dialog.
   *
   * @param {string} option Selected option between Ok and Cancel.
   */
  const handleDialogSubmit = async option => {
    if (option === OK_LABEL) {
      const error = await startDeleteCreditCard({
        paymentMethodUuid,
        billToSiteNumber,
      });
      if (!error) {
        addAlertDetails();
      }
    }
    setShowDialog(false);
  };

  /**
   * Triggers the functionality to set a default payment method
   * And displays an info message if process finished correctly
   */
  const handleSetDefaultPayment = async () => {
    const error = await startSetDefaultPayment({
      corpAcctNbr,
      paymentMethodUuid,
    });

    if (!error) {
      addAlertDetails();
    }
  };

  /**
   * Toggles the visibility of the delete confirmation dialog.
   */
  const toggleDeleteDialog = () => setShowDialog(prev => !prev);

  /**
   * Determines if the delete button should be displayed based on the payment method type and page context.
   * For Summary page, the delete button is not displayed.
   * For Payment page, the delete button is displayed for credit cards and Apple Pay wallets.
   *
   * @returns {boolean} True if the delete button should be displayed, otherwise false.
   */
  const displayDeleteButton =
    (isCreditCard(paymentMethod) || isApplePay(paymentMethod)) &&
    !isSummaryPage;

  return (
    <>
      {showDialog && (
        <DialogComponent
          callBack={handleDialogSubmit}
          contentText={getDeleteDialogText(paymentMethod)}
          positiveLabel={OK_LABEL}
          negativeLabel={CANCEL_LABEL}
        />
      )}
      <div className={blockName} data-testid="BillingLicensing-PaymentMethod">
        <Card
          className={clsx(
            `${blockName}__card`,
            isDefault && `${blockName}__card--default`
          )}
        >
          <PaymentMethodInfo
            isDrawerPage={isSummaryPage}
            paymentMethod={paymentMethod}
          />
          <PaymentMethodActions
            displayDeleteButton={displayDeleteButton}
            handleSetDefaultPayment={handleSetDefaultPayment}
            isDefault={isDefault}
            isSummaryPage={isSummaryPage}
            nextInvoiceDate={nextInvoiceDate}
            toggleDeleteDialog={toggleDeleteDialog}
          />
        </Card>
      </div>
    </>
  );
};

PaymentMethod.defaultProps = {
  billToSiteNumber: '',
  isDefault: false,
  isSummaryPage: false,
  nextInvoiceDate: '',
};

PaymentMethod.propTypes = {
  addAlertDetails: PropTypes.func.isRequired,
  billToSiteNumber: PropTypes.string,
  corpAcctNbr: PropTypes.number.isRequired,
  isDefault: PropTypes.bool,
  isSummaryPage: PropTypes.bool,
  nextInvoiceDate: PropTypes.string,
  paymentMethod: PropTypes.object.isRequired,
  startDeleteCreditCard: PropTypes.func.isRequired,
  startSetDefaultPayment: PropTypes.func.isRequired,
};

export default PaymentMethod;
